import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Package, Heart, MapPin, User, Check } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import PlantImage from '../components/fancyplants/PlantImage';
import { PLANTS } from '../data/plants';
import { SITE_IMAGES } from '../data/siteImages';

// Customer account. Orders/addresses/wishlist are illustrative placeholders here
// (they'll come from the Cosmos `orders` / `customers` containers in production);
// profile name editing uses the existing Firebase updateProfile.
const SAMPLE_ORDERS = [
  { ref: 'FP-7K2QX9', date: '12 May 2026', status: 'Delivered', total: '€54.00', items: 'Monstera Deliciosa, String of Hearts' },
  { ref: 'FP-3M8RT1', date: '28 Apr 2026', status: 'Out for delivery', total: '€89.00', items: 'Hardy Tree Fern' },
];

const STATUS_STYLE = {
  Delivered: 'bg-forest-100 text-forest-900',
  'Out for delivery': 'bg-brass/15 text-brass-deep',
  Placed: 'bg-line text-ink-soft',
};

const orderImage = (order) =>
  PLANTS.find((plant) => order.items.includes(plant.name))?.images[0] || SITE_IMAGES.account;

function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('orders');
  const [displayName, setDisplayName] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) setDisplayName(user.displayName || '');
  }, [user]);

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <Loader2 className="h-12 w-12 animate-spin text-forest-700" />
      </div>
    );
  }

  const saveName = async (e) => {
    e.preventDefault();
    setError('');
    setSaved(false);
    try {
      await updateProfile({ displayName });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.message);
    }
  };

  const signOut = async () => {
    await auth.signOut();
    navigate('/');
  };

  const tabs = [
    { id: 'orders', label: 'Orders', icon: Package },
    { id: 'wishlist', label: 'Wishlist', icon: Heart },
    { id: 'addresses', label: 'Addresses', icon: MapPin },
    { id: 'details', label: 'Details', icon: User },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-canvas pt-24">
      <header className="border-b border-line bg-canvas-raised">
        <div className="container-fp grid items-center gap-8 py-10 lg:grid-cols-[1fr_360px]">
          <div>
            <p className="eyebrow mb-2">Your account</p>
            <h1 className="t-h1">Hello{user.displayName ? `, ${user.displayName}` : ''}.</h1>
            <p className="t-lead mt-2">Manage your orders, wishlist and details.</p>
          </div>
          <PlantImage
            src={SITE_IMAGES.account}
            alt="A healthy houseplant in a bright home"
            ratio="4/3"
            className="rounded-3xl"
            priority
          />
        </div>
      </header>

      <div className="container-fp grid gap-8 py-10 lg:grid-cols-[240px_1fr]">
        {/* Side nav */}
        <aside>
          <nav className="flex gap-2 overflow-x-auto lg:flex-col">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex items-center gap-2.5 whitespace-nowrap rounded-xl px-4 py-3 text-left text-sm font-medium transition-colors ${
                  tab === id ? 'bg-forest-700 text-canvas-raised' : 'text-ink hover:bg-forest-100'
                }`}
              >
                <Icon size={18} /> {label}
              </button>
            ))}
            <button
              onClick={signOut}
              className="flex items-center gap-2.5 whitespace-nowrap rounded-xl px-4 py-3 text-left text-sm font-medium text-terracotta hover:bg-terracotta/10"
            >
              Sign out
            </button>
          </nav>
        </aside>

        <div>
          {tab === 'orders' && (
            <div className="space-y-4">
              {SAMPLE_ORDERS.map((o) => (
                <div key={o.ref} className="surface overflow-hidden">
                  <div className="grid sm:grid-cols-[140px_1fr]">
                    <PlantImage src={orderImage(o)} alt="" ratio="4/3" className="h-full" />
                    <div className="p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-display text-lg">{o.ref}</p>
                      <p className="text-sm text-ink-soft">{o.date} · {o.items}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLE[o.status] || STATUS_STYLE.Placed}`}>
                        {o.status}
                      </span>
                      <span className="t-price">{o.total}</span>
                    </div>
                    </div>
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-sm text-ink-soft">
                Looking for something new?{' '}
                <Link to="/shop" className="link-underline font-semibold">Browse the shop</Link>.
              </p>
            </div>
          )}

          {tab === 'wishlist' && (
            <div className="surface overflow-hidden text-center">
              <PlantImage src={SITE_IMAGES.plantShelf} alt="" ratio="5/2" />
              <div className="flex flex-col items-center justify-center px-6 py-10">
              <Heart size={28} className="mb-3 text-forest-500" />
              <h3 className="t-h3 mb-1">Your wishlist is empty</h3>
              <p className="mb-6 text-ink-soft">Tap the heart on any plant to save it for later.</p>
              <Link to="/shop" className="btn-forest">Find something to love</Link>
              </div>
            </div>
          )}

          {tab === 'addresses' && (
            <div className="surface overflow-hidden">
              <PlantImage src={SITE_IMAGES.delivery} alt="" ratio="5/2" />
              <div className="p-6">
              <h3 className="t-h3 mb-4">Delivery addresses</h3>
              <p className="mb-4 text-ink-soft">No saved addresses yet — you can add one at checkout.</p>
              <Link to="/shop" className="btn-outline">Start an order</Link>
              </div>
            </div>
          )}

          {tab === 'details' && (
            <div className="surface max-w-md overflow-hidden">
              <PlantImage src={SITE_IMAGES.account} alt="" ratio="5/2" />
              <div className="p-6">
              <h3 className="t-h3 mb-4">Your details</h3>
              <form onSubmit={saveName} className="space-y-4">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium">Name</span>
                  <input
                    className="fp-input"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your name"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium">Email</span>
                  <input className="fp-input bg-canvas-raised text-ink-soft" value={user.email} disabled />
                </label>
                {error && <p className="text-sm text-danger">{error}</p>}
                <button type="submit" className="btn-brass">
                  {saved ? (<><Check size={16} /> Saved</>) : 'Save changes'}
                </button>
              </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default ProfilePage;
